import { useLayoutEffect, useEffect, useRef } from 'react'
import { Canvas } from 'fabric'
import { createCanvas, disposeCanvas } from '../utils/canvasUtils'
import { CANVAS } from '../../constants'
import type { MediaDimensions } from '../types'

/**
 * Параметры для хука инициализации canvas
 */
export interface UseCanvasInitializationParams {
    canvasRef: React.RefObject<HTMLCanvasElement | null>
    fabricCanvasRef: React.MutableRefObject<Canvas | null>
    width: number
    height: number
    mediaDimensionsRef: React.MutableRefObject<MediaDimensions | null>
    setMediaDimensions: React.Dispatch<
        React.SetStateAction<MediaDimensions | null>
    >
    initialScale?: {
        canvasWidth: number
        canvasHeight: number
        originalWidth: number
        originalHeight: number
    } | null
}

/**
 * Хук для инициализации Fabric.js canvas
 * Создает canvas и настраивает его размеры
 * Использует useLayoutEffect для синхронной инициализации после DOM-мутаций
 */
export function useCanvasInitialization(
    params: UseCanvasInitializationParams
): void {
    const {
        canvasRef,
        fabricCanvasRef,
        width,
        height,
        mediaDimensionsRef,
        setMediaDimensions,
        initialScale,
    } = params

    useLayoutEffect(() => {
        // Проверяем, что canvas еще не создан
        if (fabricCanvasRef.current) {
            return
        }

        if (!canvasRef.current) {
            return
        }

        // Получаем размеры контейнера для адаптивности
        const containerElement = canvasRef.current.parentElement
        if (!containerElement) {
            return
        }

        const containerWidth = containerElement.clientWidth
        const containerHeight = containerElement.clientHeight

        // Если контейнер еще не имеет размеров, откладываем создание с повторными попытками
        if (containerWidth === 0 || containerHeight === 0) {
            let retryCount = 0
            const maxRetries = 20 // Максимум 20 попыток (около 1 секунды)
            let timeoutId: ReturnType<typeof setTimeout> | null = null
            let rafId: number | null = null

            const tryCreateCanvas = () => {
                if (!canvasRef.current || fabricCanvasRef.current) {
                    return
                }

                const retryContainerElement = canvasRef.current.parentElement
                if (!retryContainerElement) {
                    return
                }

                const retryWidth = retryContainerElement.clientWidth
                const retryHeight = retryContainerElement.clientHeight

                // Если размеры всё ещё нулевые, повторяем попытку
                if (retryWidth === 0 || retryHeight === 0) {
                    retryCount++
                    if (retryCount < maxRetries) {
                        // Повторяем через 50ms
                        timeoutId = setTimeout(() => {
                            rafId = requestAnimationFrame(tryCreateCanvas)
                        }, 50)
                    }
                    return
                }

                const currentMediaDimensions = mediaDimensionsRef.current
                // Используем сохраненный масштаб, если он есть, иначе рассчитываем размеры
                const canvasWidth = initialScale?.canvasWidth
                const canvasHeight = initialScale?.canvasHeight

                const fabricCanvas = createCanvas({
                    canvasElement: canvasRef.current,
                    containerWidth: canvasWidth ?? retryWidth,
                    containerHeight: canvasHeight ?? retryHeight,
                    minWidth: width,
                    minHeight: height,
                    mediaDimensions:
                        currentMediaDimensions ||
                        (initialScale
                            ? {
                                  width: initialScale.originalWidth,
                                  height: initialScale.originalHeight,
                              }
                            : null),
                    backgroundColor: CANVAS.BACKGROUND_COLOR,
                })

                fabricCanvasRef.current = fabricCanvas
                // Используем setTimeout, чтобы избежать обновления состояния во время рендера
                setTimeout(() => {
                    params.setMediaDimensions((prev) =>
                        prev ? { ...prev } : null
                    )
                }, 0)
            }

            // Первая попытка через requestAnimationFrame
            rafId = requestAnimationFrame(tryCreateCanvas)

            return () => {
                if (rafId !== null) {
                    cancelAnimationFrame(rafId)
                }
                if (timeoutId !== null) {
                    clearTimeout(timeoutId)
                }
            }
        }

        // Создаем Fabric.js canvas синхронно
        const currentMediaDimensions = mediaDimensionsRef.current
        // Используем сохраненный масштаб, если он есть, иначе рассчитываем размеры
        const canvasWidth = initialScale?.canvasWidth
        const canvasHeight = initialScale?.canvasHeight

        const fabricCanvas = createCanvas({
            canvasElement: canvasRef.current,
            containerWidth: canvasWidth ?? containerWidth,
            containerHeight: canvasHeight ?? containerHeight,
            minWidth: width,
            minHeight: height,
            mediaDimensions:
                currentMediaDimensions ||
                (initialScale
                    ? {
                          width: initialScale.originalWidth,
                          height: initialScale.originalHeight,
                      }
                    : null),
            backgroundColor: CANVAS.BACKGROUND_COLOR,
        })

        fabricCanvasRef.current = fabricCanvas

        // Триггерим перезапуск других хуков через обновление состояния
        // Это заставит useMediaLoading и useTextObjectManagement перезапуститься
        // Используем setTimeout, чтобы избежать обновления состояния во время рендера
        setTimeout(() => {
            params.setMediaDimensions((prev) => (prev ? { ...prev } : null))
        }, 0)

        // Cleanup при размонтировании
        return () => {
            if (fabricCanvasRef.current) {
                disposeCanvas(fabricCanvasRef.current)
                fabricCanvasRef.current = null
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Создаем canvas только один раз при монтировании

    // Применяем сохраненный масштаб после создания canvas, если он изменился
    useEffect(() => {
        if (!fabricCanvasRef.current || !initialScale) {
            return
        }

        const fabricCanvas = fabricCanvasRef.current
        const canvasElement = fabricCanvas.getElement()

        if (!canvasElement || !canvasElement.parentElement) {
            return
        }

        // Проверяем, нужно ли обновить размеры canvas
        const currentWidth = fabricCanvas.width
        const currentHeight = fabricCanvas.height

        if (
            currentWidth !== initialScale.canvasWidth ||
            currentHeight !== initialScale.canvasHeight
        ) {
            // Обновляем размеры canvas с сохраненным масштабом
            fabricCanvas.setDimensions({
                width: initialScale.canvasWidth,
                height: initialScale.canvasHeight,
            })

            // Обновляем mediaDimensions, если они еще не установлены
            if (!mediaDimensionsRef.current) {
                mediaDimensionsRef.current = {
                    width: initialScale.originalWidth,
                    height: initialScale.originalHeight,
                }
                setMediaDimensions({
                    width: initialScale.originalWidth,
                    height: initialScale.originalHeight,
                })
            }
        }
    }, [initialScale, fabricCanvasRef, mediaDimensionsRef, setMediaDimensions])
}
